// Document-level click handler for pjax navigation, ported from dux-pjax.
// Created per Pjax class (see createPjax) so the handler always talks to the
// class whose statics the app may have overridden (before/confirm/load...).

export default function createOnClick(Pjax) {
  const PjaxOnClick = {
    main(event) {
      // the page's own handler already took the click
      if (event.defaultPrevented) {
        return;
      }
      const node = event.target.closest(
        '*[click]:not([click=""]), *[href]:not([href=""]), *[pjax-refresh]:not([pjax-refresh=""])',
      );
      if (!node) {
        return;
      }

      const href = node.getAttribute('href');
      if (
        node.tagName === 'A' &&
        href?.startsWith('#') &&
        !node.hasAttribute('click') &&
        !node.hasAttribute('pjax-target') &&
        !node.hasAttribute('pjax-refresh') &&
        !node.hasAttribute('pjax-confirm')
      ) {
        return;
      }

      // A modified or non-primary click asks for a new tab / window / download.
      // A real link does that natively; for other [href] nodes we open it.
      const newTab =
        event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0;
      if (newTab && node.tagName === 'A' && href && !node.hasAttribute('click')) {
        return;
      }

      event.stopPropagation();
      event.preventDefault();

      // Snapshot the parts of the event we need; the post-confirm path may run
      // on a future tick (custom modal returning a Promise) when the original
      // MouseEvent is no longer trustworthy.
      const ctx = { node, newTab };

      const proceed = () => PjaxOnClick.execute(ctx);

      const confirmMsg = node.getAttribute('pjax-confirm');
      if (confirmMsg) {
        const result = Pjax.confirm(confirmMsg, node);
        if (result && typeof result.then === 'function') {
          result
            .then((ok) => {
              if (ok) {
                proceed();
              }
            })
            .catch((err) => Pjax.error(`confirm rejected: ${err}`));
          return;
        }
        if (!result) {
          return;
        }
      }

      proceed();
    },

    execute(ctx) {
      const node = ctx.node;

      const click = node.getAttribute('click');
      if (click) {
        return new Function(click).bind(node)();
      }

      const href = node.getAttribute('href');
      const history = node.hasAttribute('pjax-replace') ? 'replace' : undefined;
      const target = node.getAttribute('target');

      // a new-tab gesture on a non-link [href] node, before any in-page swap
      // (pjax-target / pjax-refresh) gets a chance to run
      if (ctx.newTab && href) {
        return window.open(href);
      }

      const pjaxRefresh = node.getAttribute('pjax-refresh');
      if (pjaxRefresh) {
        const targetNode = document.querySelector(pjaxRefresh);
        if (!targetNode) {
          Pjax.error(`pjax-refresh selector did not match: ${pjaxRefresh}`);
          return;
        }
        Pjax.refresh(pjaxRefresh);
        return;
      }

      const pjaxTarget = node.getAttribute('pjax-target');
      if (pjaxTarget) {
        const targetNode = document.querySelector(pjaxTarget);
        if (!targetNode) {
          Pjax.error(`pjax-target selector did not match: ${pjaxTarget}`);
          return;
        }
        Pjax.load(href, { target: targetNode, history });
        return;
      }

      if (!href) {
        return;
      }

      // an in-page anchor that went through pjax-confirm: jump like the browser
      if (href.startsWith('#')) {
        return PjaxOnClick.leave(href);
      }

      // Opt out of pjax when the link, or any ancestor, carries a no-pjax class
      // (e.g. `direct`, `no-pjax`). closest() walks up the tree so a wrapper opting
      // out covers everything inside it.
      const noPjaxSel = Pjax.config.no_pjax_class.map((cls) => `.${cls}`).join(', ');
      if (noPjaxSel && node.closest(noPjaxSel)) {
        return PjaxOnClick.leave(href, target);
      }

      if (/^javascript:/.test(href)) {
        return new Function(href.replace(/^javascript:/, ''))();
      }

      // Scheme links (mailto:, tel:, external http, vscode:), protocol-relative
      // links (//host) or links asking for a named target leave pjax too.
      if (/^\w+:/.test(href) || href.startsWith('//') || target) {
        return PjaxOnClick.leave(href, target);
      }

      Pjax.load(href, { source: node, history });
      return false;
    },

    // Leave the SPA. Open a new window only when the link declares a `target`;
    // otherwise navigate the current tab. Kept as a seam so tests can stub it -
    // DOM test environments forbid assigning window.location.
    leave(href, target) {
      if (target) {
        window.open(href, target);
      } else {
        window.location.href = href;
      }
    },
  };

  return PjaxOnClick;
}
